import { CanvasEntity, ICanvasEntity, InteractiveEntity, subscriptionListener } from '@adaptiva/canvas';
import { EOperatingSystem } from '@adaptiva/core/common';
import { Box, Vector2 } from '@adaptiva/core/space';
import { AdaColor, IThemeModel } from '@adaptiva/core/themes';
import { UI, UiService } from '@adaptiva/core/ui';
import { IMemoryPipelineNode } from '@adaptiva/data-service';
import { Injectable, inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { startWith } from 'rxjs';
import { MemoryPipelineDrawingService } from '../../services/';

@Injectable()
export class MemoryPipelineNodeEntity extends InteractiveEntity {

	public static override readonly className: string = 'MemoryPipelineNodeEntity';

	protected static get padding(): float {
		return UI.Spacing.SPACING_MEDIUM_NUMERIC * UI.BASE_UNIT_PX;
	}

	protected static get fontSize(): float {
		return UI.Spacing.SPACING_MEDIUM_NUMERIC * UI.BASE_UNIT_PX;
	}

	public static readonly ICON_DIMENSIONS: Vector2 = new Vector2(
		MemoryPipelineNodeEntity.fontSize * 1.7,
		MemoryPipelineNodeEntity.fontSize * 1.7
	);

	public override parent: MemoryPipelineNodeEntity;

	public isHiding: boolean = false;

	public node: IMemoryPipelineNode;

	public backgroundColor: AdaColor;

	public borderColor: AdaColor;

	public highlightColor: AdaColor;

	public textColor: AdaColor;

	public nodeBoundary: Box;

	public percentagePosition: Vector2;

	protected readonly ui: UiService = inject(UiService);

	protected readonly drawing: MemoryPipelineDrawingService = inject(MemoryPipelineDrawingService);

	public get padding(): float {
		return MemoryPipelineNodeEntity.padding;
	}

	public get fontSize(): float {
		return MemoryPipelineNodeEntity.fontSize;
	}

	public get iconDimensions(): Vector2 {
		return MemoryPipelineNodeEntity.ICON_DIMENSIONS;
	}

	protected static override factory(): ICanvasEntity {
		return new MemoryPipelineNodeEntity();
	}

	public override init(_from?: Partial<this>): this {
		super.init(_from);

		this.node = _from?.node;

		return this;
	}

	public override start(): void {
		super.start();
		this.determineColors();
	}

	public override beforeUpdate(_context: CanvasRenderingContext2D): void {
		super.beforeUpdate(_context);
		this.setDimensions(_context);
	}

	public override draw(_context: CanvasRenderingContext2D): void {
		if (!this.isHiding)
			this.drawing.drawNode(_context, this);

		super.draw(_context);
	}

	protected override mouseover(_position: Vector2): void {
		if (!this.isHiding)
			super.mouseover(_position);
	}

	protected override checkMouseover(_context: CanvasRenderingContext2D): boolean {
		if (this.isHiding)
			return false;

		return super.checkMouseover(_context);
	}

	// #region Drawing

	public setDimensions(_context: CanvasRenderingContext2D): void {
		const _dimensions: Vector2 = this.determineDimensions(_context);

		this.width = _dimensions.x;
		this.height = _dimensions.y;
	}

	public determineDimensions(_context: CanvasRenderingContext2D): Vector2 {
		_context.font = `${this.fontSize}px Museo Sans`;
		const _textMeasurement: TextMetrics = _context.measureText(this.node.displayText);

		return new Vector2(
			Math.floor(
				this.padding + // left padding
				this.iconDimensions.x + // space for icon
				this.padding + // spacing after icon
				_textMeasurement.width + // space for text
				this.padding // right padding
			),
			Math.floor(
				this.padding + // top padding
				this.iconDimensions.y + // content height
				this.padding // bottom padding
			)
		);
	}

	public determineColors(): void {
		this.destructibles.push(
			subscriptionListener(
				toObservable(this.engine.themeService.theme, { injector: this.injector }).pipe(
					startWith(this.engine.themeService.theme())
				).subscribe({
					next: (_theme: IThemeModel) => {
						let _osColor: AdaColor;
						const _colors: AdaColor[] = AdaColor.generatePalette(
							new AdaColor(_theme.primaryColor), 4, { hueStep: 60 }
						);

						switch (this.node.operatingSystem) {
							case (EOperatingSystem.WINDOWS): _osColor = _colors[0]; break;
							case (EOperatingSystem.LINUX): _osColor = _colors[1]; break;
							case (EOperatingSystem.MACOS): _osColor = _colors[2]; break;
							default: _osColor = _colors[3]; break;
						}

						this.borderColor = _osColor.desaturate(10);
						this.backgroundColor = AdaColor.fromRgb(_theme.backgroundColor);
						this.highlightColor = AdaColor.fromRgb(_theme.onPrimaryColor);
						this.textColor = AdaColor.fromRgb(_theme.foregroundColor);
					}
				})
			)
		);
	}

	// #endregion

	public override getBoundingBox(): Box {
		return new Box({
			topLeft: this.position.copy().subtract({
				x: this.width / 2
			}),
			bottomRight: this.position.copy().add({
				x: this.width / 2,
				y: this.height
			})
		});
	}

	protected shouldMultiSelect(): boolean {
		return false;
	}

	public override changeParent(_newParent: CanvasEntity): void {
		if (!this.parent)
			_newParent.attach(this);
		else
			super.changeParent(_newParent);
	}

}
